import { useMemo } from 'react'

import { ChevronLeft, ChevronRight, Minus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

import { cn } from '@/lib/cn'
import { useDataTableContext } from './data-table-root'

interface DataTablePaginationProps extends React.ComponentProps<'div'> {
  pageSizeOptions?: number[]
  count?: number
  isLoading?: boolean
}

export function DataTablePagination({
  pageSizeOptions = [10, 20, 30, 40, 50],
  className,
  ...props
}: DataTablePaginationProps) {
  const { table, rowCount, isLoading } = useDataTableContext()
  const { pageIndex, pageSize } = table.getState().pagination
  const pageCount = table.getPageCount()

  const { from, to } = useMemo(() => {
    const from = rowCount === 0 ? rowCount : pageIndex * pageSize + 1
    const to = Math.min(rowCount, (pageIndex + 1) * pageSize)

    return { from, to }
  }, [rowCount, pageIndex, pageSize])

  console.log(rowCount, pageIndex, pageSize)

  return (
    <div
      className={cn(
        'flex w-full items-center justify-between px-3 py-1 text-sm text-muted-foreground',
        className
      )}
      {...props}
    >
      <div className="inline-flex items-center gap-x-1 px-3 py-[5px]">
        {isLoading ? (
          <Skeleton className="h-4 w-full min-w-32" />
        ) : (
          <>
            <p>{from}</p>
            <Minus className="text-muted-foreground" />
            <p>{`${to} of ${rowCount} result`}</p>
          </>
        )}
      </div>
      <div className="flex items-center gap-x-2">
        <div className="inline-flex items-center gap-x-1 px-3 py-[5px]">
          {isLoading ? (
            <Skeleton className="h-4 w-full" />
          ) : (
            <p>
              {pageIndex + 1} of {Math.max(pageCount, 1)} pages
            </p>
          )}
        </div>
        <Button
          aria-label="Go to previous page"
          variant="outline"
          type="button"
          size="icon"
          className="size-8"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          <ChevronLeft />
        </Button>
        <Button
          type="button"
          aria-label="Go to next page"
          variant="outline"
          size="icon"
          className="size-8"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          <ChevronRight />
        </Button>
      </div>
    </div>
  )
}
